import { isEmpty } from '@sgme/fp';
import { useMemo, useState } from 'react';

import { SgPicker } from '@/components/sg-design-system/components/input/SgPicker';
import type { SgPickerItem } from '@/components/sg-design-system/components/input/SgPicker/types.ts';
import { normalizeEmailsList } from '@/utils/notificationEmails.ts';

type Props = {
  emails: string[];
  onChange: (emails: string[]) => void;
  invalid?: boolean; // we wont relay the 'valid' state (would appear as green underline, which we do not want)
  dataE2e: string;
  maxHeight?: number; // pixels. If given, tells how much height max the input should take (and scroll if needed)
};

export const EmailsInput = ({ dataE2e, emails, onChange, invalid = false, maxHeight }: Props) => {
  // This is added because the select is show under the modal in the notifications
  const [dropdownParent, setDropdownParent] = useState<HTMLElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const emailsItems = useMemo(() => emails.map((email) => ({ label: email, value: email })), [emails]);

  const handleCreateCustomItem = (rawInput: string) => {
    const email = extractEmail(rawInput).toLowerCase();

    if (!isProperEmail(email)) {
      return;
    }

    onChange(normalizeEmailsList([...emails, email]));
    setInputValue('');
  };

  const handleChange = (item: SgPickerItem<string>) => {
    // removal: clicking an email badge or its entry in the dropdown deselects it
    onChange(emails.filter((email) => email !== item.value)); // no need to normalize: operation does not create doublon nor modify order of elements
  };

  const handleClear = () => {
    onChange([]);
  };

  const handleInputChanged = (value: string) => {
    setInputValue(value.replaceAll(' ', '').toLowerCase());
  };

  const handlePaste = (pastedText: string) => {
    const pastedEmails = pastedText
      .split(/[\s,;]+/)
      .map((raw) => extractEmail(raw).toLowerCase())
      .filter(isProperEmail);

    if (isEmpty(pastedEmails)) {
      return;
    }

    onChange(normalizeEmailsList([...emails, ...pastedEmails]));
  };

  // the SgPicker now has a way if limiting its input height
  // but it still begin small and grow when we add emails
  // we must reserve the space
  const style = maxHeight ? { height: `${maxHeight}px` } : undefined;

  return (
    <div data-e2e={dataE2e} style={style} ref={setDropdownParent}>
      <SgPicker
        showInputAsCustomItem // needed to allow custom item creation
        showClearButton
        showDropdownIndicator={false}
        showIcon={false}
        showNoResultLabel={false}
        showTypingHintLabel={false}
        size="sm"
        placeholder="Type to add emails"
        inputValue={inputValue}
        inputHeight={maxHeight}
        itemSelection="multiple"
        selectedItemsDisplay="badges"
        items={emailsItems}
        selectedValues={emails}
        onChange={handleChange}
        onCreateCustomItem={handleCreateCustomItem}
        onInputChanged={handleInputChanged}
        onClear={handleClear}
        onPaste={handlePaste}
        maxBadges={0}
        validationState={invalid ? 'invalid' : undefined}
        dropdownParent={dropdownParent}
      />
    </div>
  );
};

// Extract the email address from Outlook-style strings like "First Last <email@domain.com>"
// or return the raw value trimmed if no angle brackets are present.
function extractEmail(raw: string): string {
  const trimmed = raw.trim();
  const start = trimmed.lastIndexOf('<');
  const end = trimmed.lastIndexOf('>');

  return start !== -1 && end > start ? trimmed.slice(start + 1, end).trim() : trimmed;
}

// Check that the value looks like an email: contains '@' (not at start) and a '.' in the domain.
function isProperEmail(value: string): boolean {
  const atIndex = value.lastIndexOf('@');

  if (atIndex <= 0) {
    return false;
  }

  // local part must not start or end with a dot
  const localPart = value.slice(0, atIndex);

  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return false;
  }

  const domain = value.slice(atIndex + 1);
  const lastDotIndex = domain.lastIndexOf('.');

  return lastDotIndex > 0 && lastDotIndex < domain.length - 1;
}
